import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { notification, Empty, Switch } from 'antd';
import { formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import {
  getMerchantWallets,
  getMoreMerchantWallets,
  deleteMerchantWallet,
  resetPageCounter,
  setWalletAsMain,
  resetTwoFaState,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase,
  merchantWalletsSetPrivateKeyNoTwoFa,
} from '../../redux/actions/merchantWallets';
import ReactPaginate from 'react-paginate';
import { getMerchantData } from '../../redux/actions/merchantData';
import ClientsWalletsFilters from './ClientsWalletsFilters';
import ReplenishModal from '../ReplenishModal/ReplenishModal';
import PrivateKeyModal from './PrivateKeyModal';
import Wallet from './Wallet';
import { Spin, Row, Col } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './WalletsList.scss';
import { adminApi } from '../../service/admin-api';
import { getUserMerchants } from '../../redux/actions/adminPanel/adminUsers';
import useWindowSize from '../../utils/resizeHook';
import ClientsWalletsPagination from './ClientsWalletsPagination';
import ClientsNewList from './ClientsNewList';
import { identity } from '../../utils/getIdentity';
import { useLocation } from 'react-router-dom';

const safeJsonParse = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const ClientsWalletsList = ({
  formName,
  merchantId,
  networkFilter,
  wallets,
  typeWallet,
  currencies,
  getMerchantWallets,
  getMoreMerchantWallets,
  page,
  pages,
  countItem,
  resetPageCounter,
  walletsFilters,
  fetching,
  getMerchantData,
  merchantData,
  deleteMerchantWallet,
  setWalletAsMain,
  resetTwoFaState,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase,
  type = 'user',
  userId,
  userMerchants,
}) => {
  const [walletId, setWalletId] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletCurrency, setWalletCurrency] = useState('');
  const [isReplenishModalOpen, setIsReplenishModalOpen] = useState(false);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const { t } = useTranslation();
  const [showWalletBy, setShowWalletBy] = useState(null);
  const [hideZeroCheckbox, setHideZeroCheckbox] = useState(safeJsonParse('cpayHideZeroBalancesClient', false));
  const [walletsAdmin, setWalletsAdmin] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adminPage, setAdminPage] = useState(1);
  const [adminPages, setAdminPages] = useState(1);
  const [name, setName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { currentWidth } = useWindowSize();

  const dispatch = useDispatch();

  const location = window.location.href;
  const { pathname } = useLocation();
  const [adminWalletVersion, setAdminWalletVersion] = useState(undefined);
  const walletVersionStorageKey = merchantId ? `adminClientsWalletVersion:${merchantId}` : 'adminClientsWalletVersion';
  const clientsWalletVersion =
    pathname === '/clients'
      ? 'v1'
      : type === 'admin'
        ? adminWalletVersion || (merchantData?._id === merchantId ? merchantData?.settingWallet?.version : undefined)
        : localStorage.getItem('walletVersion');
  const canSwitchWalletVersion =
    type === 'admin' &&
    merchantData?.settingWallet?.version === 'v2' &&
    merchantData?.settingWallet?.defaultSetup === 'v1';

  const isAdmin = location?.includes('admin/');

  const defaultFilterValues = (() => {
    try {
      if (!currencies || !Array.isArray(currencies)) {
        return { order: 'DESC', search: '', limit: 10, currencyIds: [], typeNetwork: networkFilter };
      }
      const result = {
        order: 'DESC',
        search: '',
        limit: 10,
        currencyIds: currencies.map(currency => currency._id),
        typeNetwork: networkFilter,
      };
      return result;
    } catch (e) {
      return { order: 'DESC', search: '', limit: 10, currencyIds: [], typeNetwork: networkFilter };
    }
  })();

  const getAdminClientsWallet = async body => {
    setLoading(true);
    try {
      const response = await adminApi.getAdminMerchantWallet(userId, merchantId, body);
      if (response?.data) {
        setAdminPage(response?.data?.page);
        setAdminPages(response?.data?.pages);
        setWalletsAdmin(response?.data?.entities);
      } else {
        setWalletsAdmin([]);
      }
      return response;
    } catch (error) {
      console.error('getAdminClientsWallet error:', error);
      setWalletsAdmin([]);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      dispatch(getUserMerchants(userId));
    }
  }, [userId]);

  useEffect(() => {
    if (type === 'admin' && merchantId) {
      const storedVersion = localStorage.getItem(walletVersionStorageKey);
      setAdminWalletVersion(storedVersion || undefined);
    }
  }, [type, merchantId]);

  useEffect(() => {
    if (type === 'admin' && merchantId && adminWalletVersion) {
      localStorage.setItem(walletVersionStorageKey, adminWalletVersion);
    }
  }, [type, merchantId, adminWalletVersion]);

  useEffect(() => {
    if (userMerchants?.length > 0 && merchantId && isAdmin) {
      const name = userMerchants?.filter(item => item?._id === merchantId);
      setName(name[0]?.name);
    } else {
      setName('');
    }
  }, [userMerchants, merchantId]);

  useEffect(() => {
    if (merchantId) {
      getMerchantData(merchantId, isAdmin ? userId : undefined);
    } else if (defaultFilterValues?.currencyIds?.length > 1 && isAdmin) {
      const { currencyIds } = defaultFilterValues;
      console.log(
        'Setting walletVersion in getAdminClientsWallet payload (useEffect 1), walletVersion:',
        clientsWalletVersion
      );
      getAdminClientsWallet({
        ...defaultFilterValues,
        order: walletsFilters?.order || 'DESC',
        currencyIds: currencyIds.join(',') || undefined,
        typeWallet: typeWallet,
        page: 1,
        positiveBalance: hideZeroCheckbox,
        walletVersion: clientsWalletVersion,
      });
    } else if (defaultFilterValues?.currencyIds?.length === 0 && isAdmin) {
      setWalletsAdmin([]);
    }
  }, [merchantId, networkFilter, hideZeroCheckbox, clientsWalletVersion]);

  useEffect(() => {
    if (defaultFilterValues?.currencyIds?.length > 1 && isAdmin && merchantData?._id === merchantId) {
      const currentCurrencyIds =
        walletsFilters?.currencyIds?.length > 0
          ? walletsFilters.currencyIds
          : defaultFilterValues.currencyIds;
      getAdminClientsWallet({
        ...defaultFilterValues,
        order: walletsFilters?.order || 'DESC',
        currencyIds: currentCurrencyIds.join(',') || undefined,
        typeWallet: typeWallet,
        page: 1,
        positiveBalance: hideZeroCheckbox,
        walletVersion: clientsWalletVersion,
      });
    } else if (defaultFilterValues?.currencyIds?.length === 0) {
      setWalletsAdmin([]);
    } else {
      console.log('[ClientsWalletsList] -> BRANCH 3: no-op (conditions not met)');
    }
  }, [walletsFilters?.order, clientsWalletVersion, currencies, merchantData]);

  useEffect(() => {
    try {
      if (
        walletsFilters &&
        typeof walletsFilters === 'object' &&
        !Array.isArray(walletsFilters) &&
        merchantId &&
        merchantId.length &&
        type !== 'admin' &&
        clientsWalletVersion
      ) {
        if (walletsFilters.limit || walletsFilters.order) {
          submitFilters();
        }
      }
    } catch (e) {
      console.error('submitFilters useEffect error:', e);
    }
  }, [merchantId, walletsFilters?.limit, walletsFilters?.order, networkFilter, hideZeroCheckbox, clientsWalletVersion]);

  const handleReplenishButton = (address, currency, isModalOpen) => {
    setWalletAddress(address);
    setWalletCurrency(currency);
    setIsReplenishModalOpen(isModalOpen);
  };

  const openNotification = () => {
    notification.success({
      message: t('copied'),
      duration: 2,
    });
  };

  const openModal = walletId => {
    setWalletId(walletId);
    setIsKeyModalOpen(true);
  };

  const closeModal = () => {
    dispatch(merchantWalletsSetPrivateKeyNoTwoFa(''));
    setIsKeyModalOpen(false);
    resetTwoFaState(false);
  };

  const loadMoreWallets = page => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      setCurrentPage(page);
      console.log(
        'Setting walletVersion in getMoreMerchantWallets payload (loadMoreWallets), walletVersion:',
        clientsWalletVersion
      );
      getMoreMerchantWallets(merchantId, {
        order: order,
        limit: limit,
        search: (search || '').trim(),
        currencyIds: currencyIds?.join(',') || undefined,
        typeWallet: typeWallet,
        page: page,
        typeNetwork: networkFilter,
        positiveBalance: hideZeroCheckbox,
        walletVersion: clientsWalletVersion,
      });
    } catch (e) {
      console.error('loadMoreWallets error:', e);
    }
  };

  const loadMoreAdminWallets = selectedPage => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      const pageNumber = selectedPage.selected + 1;
      setCurrentPage(pageNumber);
      console.log(
        'Setting walletVersion in getAdminClientsWallet payload (loadMoreAdminWallets), walletVersion:',
        clientsWalletVersion
      );
      getAdminClientsWallet({
        order: order,
        limit: limit,
        search: (search || '').trim(),
        currencyIds: currencyIds?.join(',') || undefined,
        typeWallet: typeWallet,
        page: pageNumber,
        typeNetwork: networkFilter,
        positiveBalance: hideZeroCheckbox,
        walletVersion: clientsWalletVersion,
      }).then(response => {
        if (response?.data) {
          setWalletAddress(response?.data?.entities);
        }
      });
    } catch (e) {
      console.error('loadMoreAdminWallets error:', e);
    }
  };

  const loadMoreAdminWalletsV2 = (mId, params) => {
    return getAdminClientsWallet(params);
  };

  const updateIsSegwit = () => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      console.log(
        'Setting walletVersion in getMerchantWallets payload (updateIsSegwit), walletVersion:',
        clientsWalletVersion
      );
      getMerchantWallets(merchantId, {
        order: order,
        limit: limit,
        search: (search || '').trim(),
        currencyIds: currencyIds?.join(',') || undefined,
        typeWallet: typeWallet,
        page: currentPage,
        typeNetwork: networkFilter,
        positiveBalance: hideZeroCheckbox,
        walletVersion: clientsWalletVersion,
      }).then(() => setIsFetched(true));
    } catch (e) {
      console.error('updateIsSegwit error:', e);
    }
  };

  const refreshWallets = () => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      if (type !== 'admin') {
        console.log(
          'Setting walletVersion in getMerchantWallets payload (refreshWallets non-admin), walletVersion:',
          clientsWalletVersion
        );
        getMerchantWallets(merchantId, {
          order: order,
          limit: limit,
          search: (search || '').trim(),
          currencyIds: currencyIds?.join(',') || undefined,
          typeWallet: typeWallet,
          page: currentPage,
          typeNetwork: networkFilter,
          positiveBalance: hideZeroCheckbox,
          walletVersion: clientsWalletVersion,
        });
      } else if (isAdmin) {
        console.log(
          'Setting walletVersion in getAdminClientsWallet payload (refreshWallets admin), walletVersion:',
          clientsWalletVersion
        );
        getAdminClientsWallet({
          order: order,
          limit: limit,
          search: (search || '').trim(),
          currencyIds: currencyIds?.join(',') || undefined,
          typeWallet: typeWallet,
          page: currentPage,
          typeNetwork: networkFilter,
          positiveBalance: hideZeroCheckbox,
          walletVersion: clientsWalletVersion,
        });
      }
    } catch (e) {
      console.error('refreshWallets error:', e);
    }
  };

  const submitFilters = () => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      resetPageCounter();
      if (type !== 'admin') {
        console.log(
          'Setting walletVersion in getMerchantWallets payload (submitFilters non-admin), walletVersion:',
          clientsWalletVersion
        );
        getMerchantWallets(merchantId, {
          order: order,
          limit: limit,
          search: (search || '').trim(),
          currencyIds: currencyIds?.join(',') || undefined,
          typeWallet: typeWallet,
          page: 1,
          typeNetwork: networkFilter,
          positiveBalance: hideZeroCheckbox,
          walletVersion: clientsWalletVersion,
        }).then(() => setIsFetched(true));
      } else if (isAdmin) {
        console.log(
          'Setting walletVersion in getAdminClientsWallet payload (submitFilters admin), walletVersion:',
          clientsWalletVersion
        );
        getAdminClientsWallet({
          order: order,
          limit: limit,
          search: (search || '').trim(),
          currencyIds: currencyIds?.join(',') || undefined,
          typeWallet: typeWallet,
          page: 1,
          typeNetwork: networkFilter,
          positiveBalance: hideZeroCheckbox,
          walletVersion: clientsWalletVersion,
        }).then(response => {
          if (response?.data) {
            setWalletAddress(response?.data?.entities);
          }
        });
      }
    } catch (e) {
      console.error('submitFilters error:', e);
    }
  };

  const resetFilters = () => {
    try {
      const { currencyIds } = defaultFilterValues || {};
      const safeDefaultFilterValues = defaultFilterValues || {};
      if (type !== 'admin') {
        console.log(
          'Setting walletVersion in getMerchantWallets payload (resetFilters non-admin), walletVersion:',
          clientsWalletVersion
        );
        getMerchantWallets(merchantId, {
          ...safeDefaultFilterValues,
          currencyIds: currencyIds?.join(',') || undefined,
          typeWallet: typeWallet,
          page: 1,
          typeNetwork: networkFilter,
          positiveBalance: hideZeroCheckbox,
          walletVersion: clientsWalletVersion,
        }).then(() => setIsFetched(true));
      } else if (isAdmin) {
        console.log(
          'Setting walletVersion in getAdminClientsWallet payload (resetFilters admin), walletVersion:',
          clientsWalletVersion
        );
        getAdminClientsWallet({
          ...safeDefaultFilterValues,
          currencyIds: currencyIds?.join(',') || undefined,
          page: 1,
          typeWallet: typeWallet,
          typeNetwork: networkFilter,
          positiveBalance: hideZeroCheckbox,
          walletVersion: clientsWalletVersion,
        }).then(response => {
          if (response?.data) {
            setWalletsAdmin(response?.data?.entities);
          }
        });
      }
    } catch (e) {
      console.error('resetFilters error:', e);
    }
  };

  const rawWallets = type !== 'admin' ? wallets : walletsAdmin;
  const isV2 = clientsWalletVersion === 'v2';
  const walletsToUse = !isV2
    ? rawWallets?.map(wallet => {
        if (wallet.balances) {
          const nativeIndex = wallet.balances.findIndex(b => b.currency.currencyType === 'currency');
          const primaryIndex = nativeIndex >= 0 ? nativeIndex : 0;
          const primary = wallet.balances[primaryIndex];
          const tokens = wallet.balances
            .filter((_, i) => i !== primaryIndex)
            .map(b => ({
              currency: b.currency.name,
              currencyId: b.currency.id,
              balance: { value: b.balance.value, usd: b.balance.usd },
            }));
          return {
            _id: wallet._id,
            address: wallet.address,
            currency: primary.currency.name,
            currencyId: primary.currency.id,
            currencyType: primary.currency.currencyType,
            nodeType: primary.currency.nodeType,
            balance: { value: primary.balance.value, usd: primary.balance.usd },
            typeWallet: wallet.typeWallet,
            main: wallet.main,
            tokens,
            createdAt: wallet.createdAt,
            isSegwit: wallet.isSegwit,
          };
        }
        return wallet;
      })
    : null;
  const canShowWallets = type !== 'admin' ? isFetched : !loading;

  try {
    return (
      <React.Fragment>
        <ReplenishModal
          walletAddress={walletAddress}
          walletCurrency={walletCurrency}
          isReplenishModalOpen={isReplenishModalOpen}
          handleReplenishButton={handleReplenishButton}
        />
        <PrivateKeyModal
          merchantId={merchantId}
          walletId={walletId}
          setWalletId={setWalletId}
          isKeyModalOpen={isKeyModalOpen}
          closeModal={closeModal}
          openNotification={openNotification}
          getWalletPrivateKey={getWalletPrivateKey}
          showWalletBy={showWalletBy}
          getWalletMnemonicPhrase={getWalletMnemonicPhrase}
          getPassphrase={getPassphrase}
        />
        {currencies && currencies.length && (
          <>
            {canSwitchWalletVersion && (
              <div
                style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 8, alignItems: 'center' }}
              >
                <span>Use Wallets 2.0 by default</span>
                <Switch
                  checked={clientsWalletVersion !== 'v1'}
                  onChange={value => setAdminWalletVersion(value ? 'v2' : 'v1')}
                />
              </div>
            )}
            <ClientsWalletsFilters
              formName={formName}
              merchantName={!isAdmin ? merchantData?.name : name}
              currencies={currencies}
              initialValues={defaultFilterValues}
              submitFilters={submitFilters}
              resetFilters={resetFilters}
              fetching={fetching}
              hideZeroCheckbox={hideZeroCheckbox}
              setCheckbox={setHideZeroCheckbox}
            />
          </>
        )}
        {canShowWallets && isV2 && rawWallets && rawWallets?.length > 0 ? (
          <div className={`wallets-page wallets-page--client ${fetching ? 'wallets-page_fetching' : ''}`}>
            <ClientsNewList
              type={type}
              userId={userId}
              wallets={rawWallets}
              page={type === 'admin' ? adminPage : page}
              pages={type === 'admin' ? adminPages : pages}
              fetching={fetching || loading}
              merchantId={merchantId}
              networkFilter={networkFilter}
              currencyIds={defaultFilterValues.currencyIds}
              getMoreMerchantWallets={type === 'admin' && isV2 ? loadMoreAdminWalletsV2 : getMoreMerchantWallets}
              search={walletsFilters?.search || ''}
              hideZeroCheckbox={hideZeroCheckbox}
              walletVersion={clientsWalletVersion}
              deleteMerchantWallet={deleteMerchantWallet}
              getWalletPrivateKey={getWalletPrivateKey}
              getWalletMnemonicPhrase={getWalletMnemonicPhrase}
              getPassphrase={getPassphrase}
              resetTwoFaState={resetTwoFaState}
              countItem={countItem}
              walletsFilters={walletsFilters}
              refreshWallets={refreshWallets}
              typeWallet={typeWallet}
            />
          </div>
        ) : canShowWallets && !isV2 && walletsToUse && walletsToUse?.length > 0 ? (
          <>
            <Row align="middle">
              <Col span={currentWidth >= 1024 ? 1 : 1}></Col>
              <Col sm={{ span: currentWidth >= 1024 ? 1 : 2, offset: 0 }} xs={{ span: 3, offset: 1 }}></Col>
              <Col sm={{ span: currentWidth >= 1024 ? 7 : 8 }} xs={{ span: 17 }}></Col>
              <Col sm={{ span: currentWidth >= 1024 ? 4 : 7, offset: 1 }} xs={{ span: 11, offset: 5 }}></Col>
              <Col className="wallets-page--total" sm={{ span: currentWidth >= 1024 ? 3 : 5 }} xs={{ span: 8 }}></Col>
              <Col
                sm={{ span: currentWidth >= 1024 ? 3 : 13, offset: currentWidth >= 1024 ? 0 : 3 }}
                xs={{ span: 10, offset: 5 }}
              ></Col>
              <Col
                sm={{ span: currentWidth >= 1024 ? 2 : 3, offset: currentWidth >= 1024 ? 1 : 3 }}
                xs={{ span: 6, offset: 1 }}
              ></Col>
              <Col span={currentWidth >= 1024 ? 1 : 2}></Col>
            </Row>
            <div className={`wallets-page wallets-page--client ${fetching ? 'wallets-page_fetching' : ''}`}>
              {walletsToUse.map((wallet, index) => {
                try {
                  return (
                    <Wallet
                      key={index}
                      merchantId={merchantId}
                      wallet={wallet}
                      typeWallet={typeWallet}
                      page={page}
                      pages={pages}
                      countItem={countItem}
                      walletsFilters={walletsFilters}
                      openModal={openModal}
                      openNotification={openNotification}
                      deleteMerchantWallet={deleteMerchantWallet}
                      handleReplenishButton={handleReplenishButton}
                      setWalletAsMain={setWalletAsMain}
                      currencies={currencies}
                      networkFilter={networkFilter}
                      getWalletPrivateKey={getWalletPrivateKey}
                      getWalletMnemonicPhrase={getWalletMnemonicPhrase}
                      getPassphrase={getPassphrase}
                      setShowWalletBy={setShowWalletBy}
                      updateIsSegwit={updateIsSegwit}
                      walletVersion={clientsWalletVersion}
                    />
                  );
                } catch (walletRenderError) {
                  console.error('Wallet render error:', walletRenderError);
                  return (
                    <div key={index} style={{ padding: 10, color: 'red', border: '1px solid red' }}>
                      Wallet render error: {walletRenderError.message}
                    </div>
                  );
                }
              })}
            </div>
            {!fetching && page && pages && type !== 'admin' ? (
              <ClientsWalletsPagination
                page={page}
                pages={pages}
                walletsFilters={walletsFilters}
                loadMoreWallets={loadMoreWallets}
                submitFilters={submitFilters}
                formName={formName}
              />
            ) : null}

            {adminPages > 1 && type === 'admin' ? (
              <ReactPaginate
                pageCount={adminPages}
                pageRangeDisplayed={5}
                marginPagesDisplayed={2}
                onPageChange={loadMoreAdminWallets}
                containerClassName="pagination"
                activeClassName="active"
                previousLabel={'«'}
                nextLabel={'»'}
                breakLabel={'...'}
                forcePage={adminPage - 1}
              />
            ) : null}
          </>
        ) : (
          !fetching && !loading && <Empty description={t('nodata')} />
        )}
        {(fetching || loading) && (
          <div className="wallets__loader">
            <Spin indicator={<LoadingOutlined className={`spin-${identity}`} style={{ fontSize: 24 }} spin />} />
          </div>
        )}
      </React.Fragment>
    );
  } catch (renderError) {
    console.error('ClientsWalletsList render error:', renderError);
    return (
      <div style={{ padding: 20, color: 'red' }}>
        <h3>ClientsWalletsList render error</h3>
        <pre>{renderError.message}</pre>
      </div>
    );
  }
};

const mapStateToProps = (state, props) => ({
  wallets: state.merchantWallets.data,
  page: state.merchantWallets.page,
  pages: state.merchantWallets.pages,
  countItem: state.merchantWallets.countItem,
  fetching: state.merchantWallets.fetching,
  merchantData: state.merchantData.data,
  currencies: state.currencies.data,
  walletsFilters: formValueSelector(props.formName)(state, 'search', 'order', 'limit', 'currencyIds'),
  userMerchants: state.adminUsers.userMerchants.data,
});

const mapDispatchToProps = dispatch => ({
  getMerchantWallets: getMerchantWallets(dispatch),
  getMoreMerchantWallets: getMoreMerchantWallets(dispatch),
  getMerchantData: getMerchantData(dispatch),
  deleteMerchantWallet: deleteMerchantWallet(dispatch),
  setWalletAsMain: setWalletAsMain(dispatch),
  resetTwoFaState: resetTwoFaState(dispatch),
  getWalletPrivateKey: getWalletPrivateKey(dispatch),
  resetPageCounter,
  getWalletMnemonicPhrase: getWalletMnemonicPhrase(dispatch),
  getPassphrase: getPassphrase(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientsWalletsList);
